/**
 * A NUMBER YOU CAN ACTUALLY TYPE.
 *
 * Jacky, 19 Sep: *"and edit quantity is not possible. it refresh on input right away, so i try type 2
 * digit, not possible"*. He was typing into a field whose `value` was the PARSED number: every keystroke
 * went state → parse → back into the input, so "1" was fine and the "2" that would have made twelve
 * landed in a box the component had already rewritten. Anything non-numeric ("1." on the way to "1.5",
 * a lone "-") collapsed to 0 or vanished.
 *
 * So the field keeps the DRAFT — exactly what he typed — and reports the parsed number upwards. The text
 * is only ever replaced from outside when the value changes to something the draft does not already mean
 * (a seeding button, a reset), never as an echo of his own keystrokes. `enterKeyHint`/`inputMode` keep the
 * iPad keypad numeric, which is the device this is used on.
 */
import {useEffect, useState} from 'react';
import {inputCls} from './job-bits';

/** Parse a complete non-negative money/quantity draft. Null means invalid; undefined means cleared. */
export function committedNumber(value:string):number|undefined|null{
  if(value.trim()==='')return undefined;
  const parsed=Number(value);
  return Number.isFinite(parsed)&&parsed>=0?parsed:null;
}

export function NumberField({value, onChange, className, placeholder, min = 0, step = 'any', max, disabled, title}: {
  value: number | null | undefined;
  onChange: (n: number) => void;
  className?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number | 'any';
  disabled?: boolean;
  title?: string;
}) {
  const [draft, setDraft] = useState(value === null || value === undefined ? '' : String(value));

  // Only adopt the outside value when it is not what the draft already means. Otherwise typing "1." or
  // "0.50" would be rewritten mid-keystroke, which is the bug this component exists to kill.
  useEffect(() => {
    const parsed = draft.trim() === '' ? null : Number(draft);
    if (parsed === null || !Number.isFinite(parsed) || Math.abs(parsed - Number(value ?? NaN)) > 1e-9) {
      setDraft(value === null || value === undefined ? '' : String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <input
    className={className ?? inputCls}
    type="number"
    inputMode="decimal"
    min={min}
    max={max}
    step={step}
    disabled={disabled}
    title={title}
    placeholder={placeholder}
    value={draft}
    onChange={(e) => {
      const next = e.target.value;
      setDraft(next);
      const n = Number(next);
      if (next.trim() !== '' && Number.isFinite(n)) onChange(n);
      else if (next.trim() === '') onChange(0);
    }}
    onBlur={() => {
      const n = Number(draft);
      setDraft(draft.trim() === '' || !Number.isFinite(n) ? '' : String(n));
    }}
  />;
}
